import React, { useState } from 'react';
import { Bell, BellOff, BellRing, CheckCircle, XCircle, Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNotifications } from '@/hooks/useNotifications';
import { toast } from 'sonner';

export function NotificationSettings() {
  const { 
    permission, 
    isSupported,
    isSubscribed,
    requestPermission,
    subscribeToPush,
    unsubscribeFromPush,
    testNotification,
    showNotification 
  } = useNotifications();

  const [loading, setLoading] = useState(false);

  const handleRequestPermission = async () => {
    setLoading(true);
    try {
      const result = await requestPermission();
      
      if (result === 'granted') {
        // Automatically subscribe to push after granting permission
        const subscribed = await subscribeToPush();
        
        if (subscribed) {
          toast.success('Notificações push ativadas com sucesso!');
          await showNotification('🔔 Notificações Ativadas!', {
            body: 'Você receberá alertas de validade e status dos ciclos.',
            tag: 'welcome-notification'
          });
        } else {
          toast.success('Notificações locais ativadas!');
        }
      } else if (result === 'denied') {
        toast.error('Permissão de notificação negada. Ative nas configurações do navegador.');
      } else {
        toast.info('Permissão de notificação cancelada.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const success = await subscribeToPush();
      if (success) {
        toast.success('Inscrito para notificações push!');
      } else {
        toast.error('Erro ao inscrever para notificações push.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    setLoading(true);
    try {
      const success = await unsubscribeFromPush();
      if (success) {
        toast.success('Desinscrito das notificações push.');
      } else {
        toast.error('Erro ao desinscrever das notificações.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTestNotification = async () => {
    setLoading(true);
    try {
      // Try server push first
      const serverSuccess = await testNotification();
      
      if (!serverSuccess) {
        // Fall back to local notification
        await showNotification('🧪 Teste de Notificação', {
          body: 'Se você está vendo isso, as notificações estão funcionando!',
          tag: 'test-notification'
        });
      }
      
      toast.success('Notificação de teste enviada!');
    } catch (error) {
      toast.error('Erro ao enviar notificação de teste.');
    } finally {
      setLoading(false);
    }
  };

  if (!isSupported) {
    return (
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-center gap-2 text-amber-800">
          <BellOff className="w-5 h-5" />
          <span className="font-medium">Notificações não suportadas</span>
        </div>
        <p className="text-sm text-amber-700 mt-1">
          Seu navegador não suporta notificações push. Use Chrome, Firefox ou Edge.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 bg-white border border-slate-200 rounded-lg shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BellRing className="w-5 h-5 text-blue-600" />
          <span className="font-semibold text-slate-800">Notificações Push</span>
        </div>
        
        {permission === 'granted' ? (
          isSubscribed ? (
            <span className="flex items-center gap-1 text-sm text-green-600">
              <CheckCircle className="w-4 h-4" />
              Ativadas (Push)
            </span>
          ) : (
            <span className="flex items-center gap-1 text-sm text-blue-600">
              <Bell className="w-4 h-4" />
              Ativadas (Local)
            </span>
          )
        ) : permission === 'denied' ? (
          <span className="flex items-center gap-1 text-sm text-red-600">
            <XCircle className="w-4 h-4" />
            Bloqueadas
          </span>
        ) : (
          <span className="flex items-center gap-1 text-sm text-amber-600">
            <Bell className="w-4 h-4" />
            Não configuradas
          </span>
        )}
      </div>

      <p className="text-sm text-slate-600 mb-4">
        Receba alertas de materiais próximos do vencimento, status de ciclos e agendamentos.
        {isSubscribed && (
          <span className="block mt-1 text-green-600">
            ✓ Você receberá notificações mesmo com o navegador fechado.
          </span>
        )}
      </p>

      <div className="flex flex-wrap gap-2">
        {permission === 'granted' ? (
          <>
            <Button 
              onClick={handleTestNotification} 
              variant="outline" 
              size="sm"
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Testar Notificação
            </Button>
            
            {isSubscribed ? (
              <Button 
                onClick={handleUnsubscribe} 
                variant="outline" 
                size="sm"
                disabled={loading}
                className="text-red-600 hover:text-red-700"
              >
                <BellOff className="w-4 h-4 mr-2" />
                Desativar Push
              </Button>
            ) : (
              <Button 
                onClick={handleSubscribe}
                size="sm"
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Bell className="w-4 h-4 mr-2" />
                Ativar Push
              </Button>
            )}
          </>
        ) : permission === 'denied' ? (
          <p className="text-sm text-slate-500">
            As notificações foram bloqueadas. Para reativar, acesse as configurações do navegador.
          </p>
        ) : (
          <Button 
            onClick={handleRequestPermission} 
            className="bg-blue-600 hover:bg-blue-700" 
            size="sm"
            disabled={loading}
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
            Ativar Notificações
          </Button>
        )}
      </div>
    </div>
  );
}

export default NotificationSettings;
